'use client';

import { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { MapPin, Mail, MessageCircle, Facebook, Instagram, Linkedin } from 'lucide-react';
import GetInTouchModal from './get-in-touch-modal';

export default function Footer() {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <footer className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
            {/* Brand Column */}
            <div className="col-span-1">
              <Image
                src="/spire-logo-white.png"
                alt="Spire Group"
                width={160}
                height={40}
                className="h-9 w-auto mb-4"
              />
              <p className="text-white/70 text-sm leading-relaxed">
                Your premier luxury real estate partner in Metro Manila. Exclusive listings, professional service, and exceptional properties for discerning clients.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="font-bold text-base mb-4 text-[#d60500] uppercase tracking-wide">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link href="/properties" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">All Properties</Link></li>
                <li><Link href="/browse" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Browse by Category</Link></li>
                <li><Link href="/properties?listing_type=For Sale" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">For Sale</Link></li>
                <li><Link href="/properties?listing_type=For Rent" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">For Rent</Link></li>
                <li><Link href="/about" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">About Us</Link></li>
                <li><Link href="/contact" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Contact</Link></li>
              </ul>
            </div>

            {/* Prime Locations */}
            <div>
              <h3 className="font-bold text-base mb-4 text-[#d60500] uppercase tracking-wide">Prime Locations</h3>
              <ul className="space-y-2">
                <li><Link href="/locations/makati" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Makati Luxury</Link></li>
                <li><Link href="/locations/manila" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Manila Ultra-Luxury</Link></li>
                <li><Link href="/locations/bgc" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">BGC Properties</Link></li>
                <li><Link href="/locations/alabang" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Alabang Estates</Link></li>
                <li><Link href="/locations/quezon-city" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Quezon City</Link></li>
                <li><Link href="/locations/batangas" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Batangas Resorts</Link></li>
              </ul>
            </div>

            {/* Property Types */}
            <div>
              <h3 className="font-bold text-base mb-4 text-[#d60500] uppercase tracking-wide">Property Types</h3>
              <ul className="space-y-2">
                <li><Link href="/properties?property_type=condo" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Luxury Condos</Link></li>
                <li><Link href="/properties?property_type=house" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Premium Houses</Link></li>
                <li><Link href="/properties?property_type=penthouse" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Penthouses</Link></li>
                <li><Link href="/properties?property_type=townhouse" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">Townhouses</Link></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-bold text-base mb-4 text-[#d60500] uppercase tracking-wide">Connect With Us</h3>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <MapPin className="w-4 h-4 text-[#d60500] flex-shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm">Metro Manila, Philippines</span>
                </li>
                <li className="flex gap-3">
                  <Mail className="w-4 h-4 text-[#d60500] flex-shrink-0 mt-0.5" />
                  <a href="mailto:info@spiregroup.ph" className="text-white/70 hover:text-[#d60500] transition-colors text-sm">info@spiregroup.ph</a>
                </li>
              </ul>

              {/* Social Media Links */}
              <div className="mt-6">
                <p className="text-xs text-white/50 mb-3 uppercase tracking-wide font-semibold">Follow Us</p>
                <div className="flex gap-3">
                  <a
                    href="https://facebook.com/spiregroup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-[#d60500] rounded-lg transition-all duration-200 hover:scale-110"
                    aria-label="Facebook"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                  <a
                    href="https://instagram.com/spiregroup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-[#d60500] rounded-lg transition-all duration-200 hover:scale-110"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-4 h-4" />
                  </a>
                  <a
                    href="https://linkedin.com/company/spiregroup"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 bg-white/10 hover:bg-[#d60500] rounded-lg transition-all duration-200 hover:scale-110"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-4 h-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Get in Touch CTA */}
          <div className="border-t border-white/10 pt-8 mb-8">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <h4 className="text-lg font-bold text-white mb-1">Ready to Find Your Dream Property?</h4>
                <p className="text-white/60 text-sm">Connect with our luxury real estate specialists today</p>
              </div>
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#d60500] hover:bg-[#b50400] text-white rounded-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
              >
                <MessageCircle className="w-5 h-5" />
                Get in Touch
              </button>
            </div>
          </div>

          {/* Bottom Footer */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center text-white/60 text-xs md:text-sm gap-4">
              <div className="flex items-center gap-2">
                <p>&copy; 2026 Spire Group. All Rights Reserved.</p>
              </div>
              <div className="flex gap-6">
                <Link href="/terms-of-service" className="hover:text-[#d60500] transition-colors">Terms of Service</Link>
                <Link href="/privacy-policy" className="hover:text-[#d60500] transition-colors">Privacy Policy</Link>
              </div>
            </div>
          </div>
        </div>
      </footer>

      <GetInTouchModal isOpen={showModal} onClose={() => setShowModal(false)} source="footer" />
    </>
  );
}
